import Column from '@components/Column';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import { PrepaidCardTransactionRow, PrepaidCardTransactionType } from '@models/prepaidCard';
import dayjs from 'dayjs';
import React, { HTMLAttributes, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import CancelIcon from './CancelIcon';
import ChargeIcon from './ChargeIcon';
import styles from './styles.module.scss';

export interface Props extends HTMLAttributes<HTMLDivElement>{
    isFirst?: boolean;
    transaction: PrepaidCardTransactionRow;
}

function LogItem({
    transaction, isFirst, ...rest
}: Props) {
    const {
        amount, createdAt, typeName, type,
    } = transaction;
    const { t } = useTranslation('prepaidCard');
    const color: Record<PrepaidCardTransactionType, string> = {
        CANCEL_USE: colors.gray5,
        USED: colors.red,
        CHARGE_CANCELED: colors.red,
        CHARGED: colors.green,
        REFUND_BALANCE: colors.red,
        RETURN_PURCHASE: colors.red,
    };
    const getBadge = (transactionType: PrepaidCardTransactionType) => {
        const badges: Record<PrepaidCardTransactionType, ReactNode> = {
            CHARGE_CANCELED: <CancelIcon/>,
            CHARGED: <ChargeIcon/>,
            USED: <div className={`${styles.badge} ${styles.purchase}`}>
                <Text size='s' fontWeight='bold' color={colors.gray6}>
                    {t('log.type.payment')}
                </Text>
            </div>,
            CANCEL_USE: <div className={`${styles.badge} ${styles.cancel}`}>
                <Text size='s' fontWeight='bold' color={colors.primary}>
                    {t('log.type.cancelUse')}
                </Text>
            </div>,
            REFUND_BALANCE: <div className={`${styles.badge} ${styles.cancel}`}>
                <Text size='s' fontWeight='bold' color={colors.primary}>
                    {t('log.type.refundBalance')}
                </Text>
            </div>,
            RETURN_PURCHASE: <div className={`${styles.badge} ${styles.cancel}`}>
                <Text size='s' fontWeight='bold' color={colors.primary}>
                    {t('log.type.cancelPayment')}
                </Text>
            </div>,
        };
        return badges[transactionType];
    };
    return (
        <div className={`${styles.wrapper} ${isFirst ? styles.first : ''}`} {...rest} >
            <div aria-label='prepaid-card-log'
                className={styles.container}>
                <Column className={styles.contents}
                    spacing={12}>
                    {getBadge(type)}
                    <Row fullWidth
                        justify='space-between'>
                        <Text size='xl' fontWeight='bold'>
                            {typeName}
                        </Text>
                        <Text size='xl' fontWeight='bold' color={color[type]}>
                            {type === 'CHARGED' ? '+' : '-'}
                            {amount.toLocaleString()}{t('currencyUnit')}
                        </Text>
                    </Row>
                </Column>
                <Row className={styles.footer}
                    justify='center'
                    align='center'
                    fullWidth>
                    <Text color={colors.gray6} >
                        {dayjs(createdAt).format('YYYY.MM.DD  HH:mm:ss')}
                    </Text>
                </Row>
            </div>
        </div>
    );
}

export default LogItem;
